import { useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  IMPACT_STATUS,
  IRO_CATEGORY,
  IRO_DESCRIPTION,
  NACE_CODE, NACE_SECTION,
  POSITION_ON_VALUE_CHAINS,
  REPORTING_FRAMEWORK,
  TOPIC,
} from 'constants/formFields';
import {
  DEFAULT_OPTION, IMPACT_STATUS_OPTIONS, IRO_CATEGORY_OPTIONS, IRO_VC_POSITION_OPTIONS,
} from 'constants/general';
import getNaceLabel from 'utils/getNaceLabel';
import { iroCustomCreateSchema } from 'utils/validators';

import type { TROCustomCreateSchema } from 'utils/validators';
import type ICreateIROForm from '../interfaces/ICreateIROForm';

const useCreateInvestorForm = ({
  isPageLoading, selectedItem, naceCodeTreeData, open,
}: ICreateIROForm) => {
  const handleGetSectionOption = useCallback(() => {
    const naceSection = selectedItem
      ? getNaceLabel({ naceSection: selectedItem.naceSection, naceCodeTreeData }).naceSectionLabel : '';
    return { label: naceSection || '', value: selectedItem?.naceSection || '' };
  }, [selectedItem, naceCodeTreeData]);

  const handleGetNaceCodeOption = useCallback(() => {
    const naceCode = getNaceLabel({
      naceCode: selectedItem?.naceCode,
      naceSection: selectedItem?.naceSection,
      naceCodeTreeData,
    }).naceCodeLabel;
    return { label: naceCode || '', value: selectedItem?.naceCode || '' };
  }, [selectedItem, naceCodeTreeData]);

  const {
    control,
    handleSubmit,
    formState: { isDirty, isLoading },
    reset,
    setError,
    getValues,
    watch,
    setValue,
    resetField,
  } = useForm<TROCustomCreateSchema>({
    mode: 'onSubmit',
    resolver: yupResolver(iroCustomCreateSchema),
    defaultValues: {
      [NACE_SECTION]: DEFAULT_OPTION,
      [NACE_CODE]: DEFAULT_OPTION,
      [REPORTING_FRAMEWORK]: DEFAULT_OPTION,
      [IRO_CATEGORY]: DEFAULT_OPTION,
      [IMPACT_STATUS]: DEFAULT_OPTION,
      [POSITION_ON_VALUE_CHAINS]: DEFAULT_OPTION,
      [IRO_DESCRIPTION]: '',
      [TOPIC]: DEFAULT_OPTION,
    },
  });

  useEffect(() => {
    if (open && selectedItem) {
      setValue(NACE_SECTION, handleGetSectionOption());
      setValue(NACE_CODE, handleGetNaceCodeOption());
      setValue(REPORTING_FRAMEWORK, {
        label: selectedItem.standard.name || '',
        value: String(selectedItem.standard.id) || '',
      });
      setValue(
        IRO_CATEGORY,
        IRO_CATEGORY_OPTIONS.find((item) => item.value === selectedItem.impactType) || DEFAULT_OPTION,
      );
      setValue(
        IMPACT_STATUS,
        IMPACT_STATUS_OPTIONS.find((item) => item.value === selectedItem.impact) || DEFAULT_OPTION,
      );
      setValue(IRO_DESCRIPTION, selectedItem?.description || '');
      setValue(TOPIC, {
        label: selectedItem.topic.title || '',
        value: String(selectedItem.topic.id) || '',
      });
      setValue(
        POSITION_ON_VALUE_CHAINS,
        IRO_VC_POSITION_OPTIONS.find((item) => item.value === selectedItem.valueChainSection) || DEFAULT_OPTION,
      );
    }
  }, [setValue, selectedItem, naceCodeTreeData, handleGetSectionOption, handleGetNaceCodeOption, open]);

  useEffect(() => {
    if (!isPageLoading) {
      reset();
    }
  }, [reset, isPageLoading]);

  const fieldsName = Object.keys(getValues());

  return {
    control,
    handleSubmit,
    isDirty,
    reset,
    isLoading,
    setError,
    fieldsName,
    watch,
    setValue,
    resetField,
  };
};

export default useCreateInvestorForm;
